import { useEffect } from "react"

import { type GroundSpec, useGroundStore } from "./store"

/*
 * A page claims the ground for as long as it is mounted: a `resolve` spec once
 * it has a mappable point. Pages that never call this sit on the still the
 * console shell puts under everything (`useGroundShell`); `STILL` is only
 * claimed to insist on it.
 *
 * The claim is keyed on the spec's content, so a page re-rendering with equal
 * props does not disturb the ground. The release is deferred a tick: Inertia
 * unmounts the old page and mounts the new one in the same commit, and the
 * ground must see the next claim, never a null in between — that null would
 * unmount the live map the next page is about to resolve onto.
 */
export function useGround(spec: GroundSpec | null) {
  const key = JSON.stringify(spec)

  useEffect(() => {
    const parsed = JSON.parse(key) as GroundSpec | null
    if (!parsed) return

    const owner = Symbol("ground")
    useGroundStore.getState().claim(owner, parsed)

    return () => {
      // by the time this fires the next page has claimed, and a release from
      // a former owner is a no-op — only a page that really left lets go
      setTimeout(() => useGroundStore.getState().release(owner), 0)
    }
  }, [key])
}

/*
 * The console shell registers itself for as long as it is mounted, and the
 * store serves the still under every page it holds. It is a count, not a
 * flag: a visit mounts the next page's shell before the old one has gone.
 */
export function useGroundShell() {
  useEffect(() => {
    const { enterShell, leaveShell } = useGroundStore.getState()
    enterShell()
    return leaveShell
  }, [])
}
